import {
    EntityRegionModeOutput,
    RegionId,
    rootRegionParentId,
    TenantRegion,
} from "@sp-crm/core";

export interface RegionTreeNode {
    region: TenantRegion;
    children: RegionTreeNode[];
    depth: number;
}

export interface RegionVisibility {
    regionId: RegionId;
    mode: EntityRegionModeOutput;
}

export interface VisibleRegion {
    region: TenantRegion;
    mode: EntityRegionModeOutput;
}

export interface Preset {
    label: string;
    visibility: RegionVisibility[];
}

export function buildRegionTree(regions: TenantRegion[]): RegionTreeNode[] {
    const regionMap = new Map<RegionId, RegionTreeNode>();
    const roots: RegionTreeNode[] = [];

    regions.forEach(region => {
        regionMap.set(region.id, { region, children: [], depth: 0 });
    });

    regions.forEach(region => {
        const node = regionMap.get(region.id);
        if (!node) return;

        if (region.parentRegionId === rootRegionParentId) {
            roots.push(node);
        } else {
            const parent = regionMap.get(region.parentRegionId);
            if (parent) {
                parent.children.push(node);
            } else {
                roots.push(node);
            }
        }
    });

    function setDepth(node: RegionTreeNode, depth: number) {
        node.depth = depth;
        node.children.sort((a, b) => a.region.name.localeCompare(b.region.name));
        node.children.forEach(child => setDepth(child, depth + 1));
    }

    roots.sort((a, b) => a.region.name.localeCompare(b.region.name));
    roots.forEach(root => setDepth(root, 0));

    return roots;
}

export function findNode(
    tree: RegionTreeNode[],
    regionId: RegionId,
): RegionTreeNode | null {
    for (const node of tree) {
        if (node.region.id === regionId) {
            return node;
        }
        const found = findNode(node.children, regionId);
        if (found) {
            return found;
        }
    }
    return null;
}

export function getDescendantIds(node: RegionTreeNode): RegionId[] {
    const descendants: RegionId[] = [];

    function collect(n: RegionTreeNode) {
        n.children.forEach(child => {
            descendants.push(child.region.id);
            collect(child);
        });
    }

    collect(node);
    return descendants;
}

export function getAllDescendantIds(
    tree: RegionTreeNode[],
    regionId: RegionId,
): RegionId[] {
    const node = findNode(tree, regionId);
    if (!node) return [];
    return getDescendantIds(node);
}

export function computeVisibleRegions(
    allRegions: TenantRegion[],
    visibility: RegionVisibility[],
): Map<RegionId, EntityRegionModeOutput> {
    const result = new Map<RegionId, EntityRegionModeOutput>();
    const tree = buildRegionTree(allRegions);

    visibility.forEach(({ regionId, mode }) => {
        if (mode === EntityRegionModeOutput.IncludeDescendants) {
            result.set(regionId, EntityRegionModeOutput.IncludeDescendants);
            const descendants = getAllDescendantIds(tree, regionId);
            descendants.forEach(id => {
                if (!result.has(id)) {
                    result.set(id, EntityRegionModeOutput.Inherited);
                }
            });
        } else if (mode === EntityRegionModeOutput.LocalOnly) {
            result.set(regionId, EntityRegionModeOutput.LocalOnly);
        }
    });

    return result;
}

export function getVisibilityMode(
    visibility: RegionVisibility[],
    visibleRegionsMap: Map<RegionId, EntityRegionModeOutput>,
    regionId: RegionId,
): EntityRegionModeOutput | null {
    const explicitEntry = visibility.find(v => v.regionId === regionId);
    if (explicitEntry) {
        const mode = explicitEntry.mode as string;
        if (mode === EntityRegionModeOutput.IncludeDescendants) {
            return EntityRegionModeOutput.IncludeDescendants;
        }
        if (mode === EntityRegionModeOutput.Inherited) {
            if (visibleRegionsMap.get(regionId) === EntityRegionModeOutput.Inherited) {
                return EntityRegionModeOutput.Inherited;
            }
            return null;
        }
        return EntityRegionModeOutput.LocalOnly;
    }

    const inherited = visibleRegionsMap.get(regionId);
    if (inherited === EntityRegionModeOutput.Inherited) {
        return EntityRegionModeOutput.Inherited;
    }

    return null;
}

export function generatePresets(tree: RegionTreeNode[], tenantName?: string): Preset[] {
    const presets: Preset[] = [];

    if (tree.length === 1) {
        const root = tree[0];
        presets.push({
            label: "Tenant-wide",
            visibility: [
                {
                    regionId: root.region.id,
                    mode: EntityRegionModeOutput.IncludeDescendants,
                },
            ],
        });

        if (root.children.length > 0) {
            presets.push({
                label: `${root.region.name} only`,
                visibility: [
                    {
                        regionId: root.region.id,
                        mode: EntityRegionModeOutput.LocalOnly,
                    },
                ],
            });
        }
    }

    function addPresetsForNode(node: RegionTreeNode, maxDepth: number) {
        if (node.depth >= maxDepth) return;
        if (node.children.length === 0) return;

        node.children.forEach(child => {
            if (child.children.length > 0) {
                presets.push({
                    label: `${child.region.name} only`,
                    visibility: [
                        {
                            regionId: child.region.id,
                            mode: EntityRegionModeOutput.LocalOnly,
                        },
                    ],
                });
            }
        });

        node.children.forEach(child => {
            addPresetsForNode(child, maxDepth);
        });
    }

    tree.forEach(root => addPresetsForNode(root, 2));

    return presets.slice(0, 8);
}

export function flattenTree(tree: RegionTreeNode[]): RegionTreeNode[] {
    const result: RegionTreeNode[] = [];

    function flatten(nodes: RegionTreeNode[]) {
        nodes.forEach(node => {
            result.push(node);
            flatten(node.children);
        });
    }

    flatten(tree);
    return result;
}

export function getVisibilitySummary(
    allRegions: TenantRegion[],
    visibility: RegionVisibility[],
): { totalVisible: number; explicitCount: number; regionNames: string[] } {
    const visibleMap = computeVisibleRegions(allRegions, visibility);

    const visibleRegionIds = Array.from(visibleMap.keys());
    const regionNames = visibleRegionIds
        .map(id => allRegions.find(r => r.id === id)?.name)
        .filter((name): name is string => !!name)
        .sort((a, b) => a.localeCompare(b));

    return {
        totalVisible: visibleRegionIds.length,
        explicitCount: visibility.length,
        regionNames,
    };
}

export function cycleVisibilityMode(
    currentVisibility: RegionVisibility[],
    regionId: RegionId,
    currentMode: EntityRegionModeOutput | null,
    hasChildren: boolean,
    descendantIds: RegionId[],
    isRootRegion: boolean,
): RegionVisibility[] {
    if (currentMode === EntityRegionModeOutput.Inherited) {
        return currentVisibility;
    }

    if (currentMode === null) {
        const newMode = isRootRegion
            ? EntityRegionModeOutput.IncludeDescendants
            : EntityRegionModeOutput.LocalOnly;

        let baseVisibility = currentVisibility;
        if (isRootRegion) {
            const descendantSet = new Set(descendantIds);
            baseVisibility = currentVisibility.filter(
                v => !descendantSet.has(v.regionId),
            );
        }

        const existingIndex = baseVisibility.findIndex(v => v.regionId === regionId);
        if (existingIndex >= 0) {
            return baseVisibility.map((v, i) =>
                i === existingIndex ? { ...v, mode: newMode } : v,
            );
        }
        return [...baseVisibility, { regionId, mode: newMode }];
    }

    if (currentMode === EntityRegionModeOutput.LocalOnly) {
        if (hasChildren) {
            const descendantSet = new Set(descendantIds);
            return currentVisibility
                .filter(v => !descendantSet.has(v.regionId))
                .map(v =>
                    v.regionId === regionId
                        ? { ...v, mode: EntityRegionModeOutput.IncludeDescendants }
                        : v,
                );
        } else {
            return currentVisibility.filter(v => v.regionId !== regionId);
        }
    }

    if (currentMode === EntityRegionModeOutput.IncludeDescendants) {
        return currentVisibility.filter(v => v.regionId !== regionId);
    }

    return currentVisibility;
}
